import React, { Dispatch, SetStateAction, useState } from "react";
import Select from "react-select";
import { FormGroup } from "reactstrap";

import { Switch } from "../../../../shared/Switch/Switch";
import { OrganizationSettingsModal } from "../../shared/OrganizationSettingsModal";
import { CustomerSettingsVariables } from "../../InsightsCard/Insights/Insights";
import { tooltips } from "../../../../../lib/utils/tooltips";
import { selectStyles } from "../../../../../lib/utils/selectStyles";
import { LabelWithInformationBox } from "../../../../shared/LabelWithInformationBox/LabelWithInformationBox";
import { Role } from "../../../../../api/grpc/account/account";
import { InputDescription } from "../../../../shared/InputDescription/InputDescription";

const bookingRoleOptions = [
  { label: "Member", value: "member" },
  { label: "Coordinator", value: "coordinator" },
] as const;

type BookingRoleOption = (typeof bookingRoleOptions)[number];
type BookingRoleValue = BookingRoleOption["value"];

const roleByValue: Record<BookingRoleValue, Role> = {
  member: Role.MEMBER,
  coordinator: Role.COORDINATOR,
};

const defaultBookingRoles = bookingRoleOptions.map(
  (option) => option.value
) as BookingRoleValue[];

const getAllowedRoleValues = (
  disabledRoles: Role[] | undefined,
  enabled: boolean | undefined
): BookingRoleValue[] => {
  if (!enabled) {
    return [];
  }

  const disabledSet = new Set(disabledRoles ?? []);

  return bookingRoleOptions
    .map((option) => option.value)
    .filter((value) => !disabledSet.has(roleByValue[value]));
};

const getDisabledRoleValues = (allowedRoles: BookingRoleValue[]) => {
  const allowedSet = new Set(allowedRoles);

  return bookingRoleOptions
    .map((option) => option.value)
    .filter((value) => !allowedSet.has(value))
    .map((value) => roleByValue[value]);
};

interface Props {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  handleDeskSimultaneous: (values: CustomerSettingsVariables) => void;
  disableDeskSimultaneous?: boolean;
  deskBookingDisabledForRoles?: Role[];
  deskBookingEnabled?: boolean;
  roomBookingDisabledForRoles?: Role[];
  roomBookingEnabled?: boolean;
  error?: Error;
}

export const BookingsSettingsModal = ({
  isOpen,
  setIsOpen,
  loading,
  handleDeskSimultaneous,
  disableDeskSimultaneous,
  deskBookingDisabledForRoles,
  deskBookingEnabled,
  roomBookingDisabledForRoles,
  roomBookingEnabled,
  error,
}: Props) => {
  const [disableDeskSimultaneousToChange, setDisableDeskSimultaneousToChange] =
    useState<boolean | undefined>(undefined);
  const [enableRoomBooking, setEnableRoomBooking] = useState(
    !!roomBookingEnabled
  );
  const [enableDeskBooking, setEnableDeskBooking] = useState(
    !!deskBookingEnabled
  );
  const [roomBookingRoles, setRoomBookingRoles] = useState<BookingRoleValue[]>(
    getAllowedRoleValues(roomBookingDisabledForRoles, roomBookingEnabled)
  );
  const [deskBookingRoles, setDeskBookingRoles] = useState<BookingRoleValue[]>(
    getAllowedRoleValues(deskBookingDisabledForRoles, deskBookingEnabled)
  );

  const disableDeskSimultaneousValue =
    disableDeskSimultaneousToChange !== undefined
      ? disableDeskSimultaneousToChange
      : !!disableDeskSimultaneous;

  const handleDisableDeskSimultaneousChange = (value: boolean) => {
    setDisableDeskSimultaneousToChange(value);
  };

  const handleRoomBookingChange = (value: boolean) => {
    setEnableRoomBooking(value);
    setRoomBookingRoles(value ? defaultBookingRoles : []);
  };

  const handleDeskBookingChange = (value: boolean) => {
    setEnableDeskBooking(value);
    setDeskBookingRoles(value ? defaultBookingRoles : []);
  };

  const isRoomRolesMissing = enableRoomBooking && roomBookingRoles.length === 0;
  const isDeskRolesMissing = enableDeskBooking && deskBookingRoles.length === 0;
  const hasRoleSelectionError = isRoomRolesMissing || isDeskRolesMissing;

  return (
    <OrganizationSettingsModal
      title="Bookings"
      formTitle="Manage global settings for user bookings. Admins are always allowed to create bookings."
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      loading={loading}
      error={error}
      formClassName="d-block"
      modalClassName="Bookings"
      disabled={hasRoleSelectionError}
      toggleModal={() => {
        setIsOpen(false);
        return;
      }}
      handleSave={() => {
        handleDeskSimultaneous({
          disableDeskSimultaneous: disableDeskSimultaneousToChange,
          deskBookingDisabledForRoles: getDisabledRoleValues(deskBookingRoles),
          deskBookingEnabled: enableDeskBooking,
          roomBookingDisabledForRoles: getDisabledRoleValues(roomBookingRoles),
          roomBookingEnabled: enableRoomBooking,
        });
        setIsOpen(false);
        return;
      }}
    >
      <Switch
        name="enableRoomBooking"
        value={enableRoomBooking}
        onChange={handleRoomBookingChange}
        title="Enable room booking"
        message={tooltips.organizationSettings.generalSettings.roomBooking}
        className="mb-3"
      >
        <div className="mt-3">
          <FormGroup>
            <LabelWithInformationBox
              for="roomBookingRoles"
              title="Allow for:"
            />
            <Select
              isMulti
              isClearable
              id="roomBookingRoles"
              isSearchable={true}
              name="roomBookingRoles"
              theme={selectStyles}
              options={bookingRoleOptions}
              onChange={(e) => {
                if (!e) {
                  setRoomBookingRoles([]);
                  return;
                }

                const values = e.map(
                  (item) => item.value
                ) as BookingRoleValue[];

                setRoomBookingRoles(values);
              }}
              value={bookingRoleOptions.filter((item) =>
                roomBookingRoles.includes(item.value)
              )}
            />
          </FormGroup>
          {isRoomRolesMissing && (
            <InputDescription
              title="If enabled, at least one role must be added."
              className="mb-2"
              color="danger"
            />
          )}
        </div>
      </Switch>

      <Switch
        name="enableDeskBooking"
        value={enableDeskBooking}
        onChange={handleDeskBookingChange}
        title="Enable desk booking"
        message={tooltips.organizationSettings.generalSettings.deskBooking}
        className="mb-3"
      >
        <div className="mt-3">
          <FormGroup>
            <LabelWithInformationBox
              for="deskBookingRoles"
              title="Allow for:"
            />
            <Select
              isMulti
              isClearable
              id="deskBookingRoles"
              isSearchable={true}
              name="deskBookingRoles"
              theme={selectStyles}
              options={bookingRoleOptions}
              onChange={(e) => {
                if (!e) {
                  setDeskBookingRoles([]);
                  return;
                }

                const values = e.map(
                  (item) => item.value
                ) as BookingRoleValue[];

                setDeskBookingRoles(values);
              }}
              value={bookingRoleOptions.filter((item) =>
                deskBookingRoles.includes(item.value)
              )}
            />
          </FormGroup>
          {isDeskRolesMissing && (
            <InputDescription
              title="If enabled, at least one role must be added."
              className="mb-2"
              color="danger"
            />
          )}
        </div>
      </Switch>

      <Switch
        name="disableDeskSimultaneous"
        value={disableDeskSimultaneousValue}
        onChange={handleDisableDeskSimultaneousChange}
        title="Don't allow simultaneous reservations of desks"
        message={
          tooltips.organizationSettings.generalSettings.disableSimultaneous
        }
        className="mb-3 WorkingHoursForm__selectors--deskController"
      />
    </OrganizationSettingsModal>
  );
};
